import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpenText } from 'lucide-react';

/**
 * Floating left-edge "Knowledge Base" tab that sits at vertical-middle on
 * every page except the KB list page and the admin section.
 *
 * Engagement-nudge: once per session, if the visitor scrolls past 50% of a
 * page without ever having clicked the tab, we fire a single teal→soft-amber
 * pulse. Draws peripheral attention without being annoying. Session-scoped
 * so the same visitor isn't pulsed on every page they navigate to.
 */
const PULSE_STORAGE_KEY = 'kb-tab-pulsed';
const CLICK_STORAGE_KEY = 'kb-tab-clicked';

const KnowledgeBaseTab = () => {
  const { pathname } = useLocation();
  const [pulse, setPulse] = useState(false);
  const listenerCleanup = useRef(null);

  // Hide only on the KB list itself and admin — deliberately KEEP the tab
  // visible on individual /tmd-knowledge-base/{slug} article pages so patients
  // reading one article can jump back to the wider library. Old /knowledge-base
  // paths still count while the 301 redirects are catching stragglers.
  const isKBList = (
    pathname === '/tmd-knowledge-base' || pathname === '/tmd-knowledge-base/' ||
    pathname === '/knowledge-base' || pathname === '/knowledge-base/'
  );
  const isAdmin = pathname.startsWith('/admin');
  const isArticle = (
    pathname.startsWith('/tmd-knowledge-base/') || pathname.startsWith('/knowledge-base/')
  ) && !isKBList;
  const hidden = isKBList || isAdmin;

  useEffect(() => {
    if (hidden) return;
    // Skip if the visitor has ever clicked the tab OR already been pulsed
    // in this browser session — we don't want to re-pulse on every route.
    try {
      if (
        sessionStorage.getItem(PULSE_STORAGE_KEY) === '1' ||
        sessionStorage.getItem(CLICK_STORAGE_KEY) === '1'
      ) {
        return;
      }
    } catch { /* private-mode / storage disabled — proceed harmlessly */ }

    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total <= 0) return;
      // 50% of the way through the page
      if (scrolled / total >= 0.5) {
        setPulse(true);
        try { sessionStorage.setItem(PULSE_STORAGE_KEY, '1'); } catch { /* noop */ }
        // Auto-remove class after one animation cycle so the pill returns to rest.
        setTimeout(() => setPulse(false), 2400);
        window.removeEventListener('scroll', onScroll);
        listenerCleanup.current = null;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    listenerCleanup.current = () => window.removeEventListener('scroll', onScroll);
    return () => listenerCleanup.current && listenerCleanup.current();
  }, [hidden, pathname]);

  if (hidden) return null;

  const handleClick = () => {
    try { sessionStorage.setItem(CLICK_STORAGE_KEY, '1'); } catch { /* noop */ }
  };

  return (
    <Link
      to="/tmd-knowledge-base"
      className={`kb-floating-tab${pulse ? ' kb-floating-tab--pulse' : ''}`}
      data-testid="knowledge-base-floating-tab"
      data-pulse={pulse ? 'on' : 'off'}
      aria-label={isArticle ? 'Browse all TMD Knowledge Base articles' : 'Open the TMD Knowledge Base'}
      onClick={handleClick}
    >
      <BookOpenText size={16} className="kb-floating-tab-icon" />
      <span className="kb-floating-tab-label">
        {isArticle ? (
          <>More Articles — Read <strong>HERE</strong></>
        ) : (
          <>TMD Knowledge Base — Learn <strong>HERE</strong></>
        )}
      </span>
    </Link>
  );
};

export default KnowledgeBaseTab;
